// Shared helper for fetching collection JSON from the rb-sheets CDN.
// Supports both manifest v2 (preferred) and v1 (legacy). Shared by seed.client / vxshop.

import { masterDataChannel } from '@rb/shared/master-data/cdn-config';
import { getSheetsBase } from '@/lib/cdnBase';

/** <origin>/rb-sheets — origin injected via VITE_R2_PUBLIC_BASE (hard-fail if unset). */
export function cdnBaseUrl(): string {
  return getSheetsBase();
}

/**
 * EnvSchema('production') → R2 채널 슬러그('prod'). apply EF 가 prod 데이터를 'prod'
 * 채널에 발행하므로 R2 경로를 만들 때 매핑한다. dev/staging 은 identity. DB 스키마
 * 인자는 'production' 그대로 두고 경로를 만들 때만 이 함수를 거친다.
 */
export function cdnChannel(env: string): string {
  return masterDataChannel(env);
}

interface ManifestV1 {
  version: string;
}
interface ManifestV2 {
  // schemaVersion 2 (denormalized) and 3 (normalized) share the same
  // {name: {version, hash}} collections shape — both handled here.
  schemaVersion: 2 | 3;
  updatedAt?: string;
  collections: Record<string, { version: string; hash?: string }>;
}
export type Manifest = ManifestV1 | ManifestV2;

export function isManifestV2(m: Manifest): m is ManifestV2 {
  const sv = (m as ManifestV2).schemaVersion;
  return (sv === 2 || sv === 3) && typeof (m as ManifestV2).collections === 'object';
}

export async function fetchManifest(env: string): Promise<Manifest> {
  const url = `${cdnBaseUrl()}/${cdnChannel(env)}/manifest.json`;
  const res = await fetch(url, { cache: 'no-store' });
  if (!res.ok) throw new Error(`Failed to fetch sheet manifest for env "${env}" (${res.status})`);
  return (await res.json()) as Manifest;
}

/**
 * server-manifest.json 을 fetch. 없으면(404) combined manifest.json 으로 폴백한다.
 * GS-authored env(server-manifest 미생성)도 깨지지 않게 하는 호환 경로.
 */
export async function fetchServerManifest(env: string): Promise<Manifest> {
  const res = await fetch(`${cdnBaseUrl()}/${cdnChannel(env)}/server-manifest.json`, {
    cache: 'no-store',
  });
  if (res.ok) return (await res.json()) as Manifest;
  if (res.status === 404) return fetchManifest(env);
  throw new Error(`Failed to fetch server manifest for env "${env}" (${res.status})`);
}

export function resolveVersion(manifest: Manifest, filename: string, label?: string): string {
  if (isManifestV2(manifest)) {
    const entry = manifest.collections[filename];
    if (!entry) {
      throw new Error(
        `"${label ?? filename}" (CDN filename: "${filename}") not found in manifest v2 collections`
      );
    }
    return entry.version;
  }
  return manifest.version;
}

/** Resolves the manifest then fetches a single collection JSON. */
export async function fetchCdnCollection<T = unknown>(
  env: string,
  collectionName: string
): Promise<T> {
  const manifest = await fetchManifest(env);
  const version = resolveVersion(manifest, collectionName);
  const url = `${cdnBaseUrl()}/${cdnChannel(env)}/${collectionName}.${version}.json`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Failed to fetch ${url} (${res.status})`);
  return (await res.json()) as T;
}
